import { ToggleGroup, Options } from '@instructure/ui';
import { useState, useMemo, useCallback } from 'react';
import InfoIcon from '@ui/shared/components/Common/InfoIcon';
import {
    Badge,
    Flex,
    View,
    IconCheckDarkLine,
    IconVideoCameraLine,
    IconButton,
    IconSettingsLine,
    Text,
    IconNoteSolid,
    IconEyeLine,
    Tooltip,
    Pill,
    Popover,
    Button,
} from '@smarterservices/smarter-ui';
import useAppStore from '../../stores/app';
import useSessionStore from '../../stores/session';
import RenderName from './RenderName';
import TimeElapsed from '../Common/TimeElapsed';

/**
 * AwaitingCheckin Component
 * 
 * Displays a collapsible list of sessions that are awaiting check-in.
 * Shows a timer for how long each session has been waiting, the student's name,
 * and provides options to start check-in or transfer the session.
 * 
 * @param {Object} props - Component props
 * @param {boolean} [props.expanded=true] - Whether the toggle group is expanded by default
 * @param {Function} props.onMenuDismiss - Callback to close the parent popover
 * @returns {JSX.Element} The awaiting check-in component
 */
const RecentlyExited = ({ expanded = true, onMenuDismiss }) => {
    const checkout = useSessionStore((state) => state.checkout);
    const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);
    const [openPopoverSessionSid, setOpenPopoverSessionSid] = useState(null);
    const [confirmClearSessionSid, setConfirmClearSessionSid] = useState(null);

    /**
     * Sort checkout sessions by inCheckoutAt timestamp (newest to oldest)
     * Sessions without inCheckoutAt are treated as 0 and sorted to the end
     */

    const sortedCheckout = Object.entries(checkout).sort(([, a], [, b]) => {
        const timeA = a.inCheckoutAt || 0;
        const timeB = b.inCheckoutAt || 0;
        return timeB - timeA; // Descending order (newest first)
    });

    /**
     * Handles the clear session click - shows confirmation
     */
    const handleClearSessionClick = useCallback((sessionSid) => {
        setConfirmClearSessionSid(sessionSid);
    }, []);

    /**
     * Handles the confirmed clear session action
     */
    const handleConfirmClearSession = useCallback((sessionSid) => {
        useSessionStore.getState().removeFromCheckoutQueue(sessionSid);
        setConfirmClearSessionSid(null);
        setOpenPopoverSessionSid(null); // Close popover
    }, []);

    /**
     * Handles popover close - resets confirmation state and closes parent popover
     */
    const handlePopoverClose = useCallback(() => {
        setOpenPopoverSessionSid(null);
        setConfirmClearSessionSid(null);
        if (onMenuDismiss) {
            onMenuDismiss();
        }
    }, [onMenuDismiss]);

    /**
     * Renders the options popover for a session
     * Memoized to prevent stale closures over checkout state
     * @param {string} sessionSid - The session identifier
     * @returns {JSX.Element} Popover with session options
     */
    const renderOptionsMenu = useCallback((sessionSid) => {
        const isOpen = openPopoverSessionSid === sessionSid;
        const isConfirming = confirmClearSessionSid === sessionSid;

        return (
            <Popover
                isShowingContent={isOpen}
                onShowContent={() => setOpenPopoverSessionSid(sessionSid)}
                onHideContent={handlePopoverClose}
                on="click"
                placement="bottom end"
                shouldContainFocus
                shouldReturnFocus
                renderTrigger={
                    <IconButton
                        withBackground={false}
                        withBorder={false}
                        color="primary"
                        size="small"
                        screenReaderLabel="Session Options"
                        margin="none"
                        padding="none"
                    >
                        <IconSettingsLine />
                    </IconButton>
                }
            >
                <View as="div" padding="none" minWidth="180px">
                    <View as="div">
                        {/* Review Session option */}
                        <View
                            as="div"
                            padding="small"
                            cursor="pointer"
                            background="primary"
                            onClick={() => {
                                const session = checkout[sessionSid];
                                const token = useAppStore.getState().user.token;
                                const url = new URL(`/vtc/installs/${session.installSid}/exams/${session.examSid}/session/${sessionSid}`, window.location.origin);
                                url.searchParams.set('token', token);
                                window.open(url.toString(), sessionSid, 'width=800,height=600,scrollbars=yes,resizable=yes');
                                handlePopoverClose();
                            }}
                            style={{
                                cursor: 'pointer',
                                transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <Flex alignItems="center">
                                <Flex.Item margin="0 small 0 0">
                                    <IconVideoCameraLine size="x-small" />
                                </Flex.Item>
                                <Flex.Item>
                                    <Text size="medium">Review Session</Text>
                                </Flex.Item>
                            </Flex>
                        </View>
                        {/* Clear Session option with confirmation */}
                        <View
                            as="div"
                            padding="small"
                            cursor="pointer"
                            background="primary"
                            onClick={() => {
                                if (isConfirming) {
                                    handleConfirmClearSession(sessionSid);
                                } else {
                                    handleClearSessionClick(sessionSid);
                                }
                            }}
                            style={{
                                cursor: 'pointer',
                                transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <Flex alignItems="center">
                                <Flex.Item margin="0 small 0 0">
                                    <IconCheckDarkLine size="x-small" />
                                </Flex.Item>
                                <Flex.Item>
                                    <Text size="medium">{isConfirming ? 'Sure? Yes' : 'Clear Session'}</Text>
                                </Flex.Item>
                            </Flex>
                        </View>
                    </View>
                </View>
            </Popover>
        );
    }, [checkout, openPopoverSessionSid, confirmClearSessionSid, handlePopoverClose, handleClearSessionClick, handleConfirmClearSession]);

    const checkoutCount = Object.keys(checkout).length;

    return (
        <ToggleGroup
            toggleLabel="Sessions that have recently ended"
            padding="0 0 xxx-small 0"
            defaultExpanded={expanded}
            summary={
                <Flex justifyItems="space-between" margin="none small none none">
                    <Flex.Item>
                        Recently Ended
                        <InfoIcon margin="0 0 xxx-small xx-small" color="secondary" tip="This includes sessions that have recently ended and exited the room. Transferred sessions will not be included here." size="xx-small" />
                    </Flex.Item>
                    {checkoutCount > 0 && (
                        <Flex.Item>
                            <Badge
                                count={checkoutCount}
                                variant="danger"
                                pulse={true}
                                standalone
                            />
                        </Flex.Item>
                    )}
                </Flex>
            }
        >
            {checkoutCount > 0 ? (
                <View as="div" overflowY="auto" maxHeight="50vh">
                    <Options>
                        {sortedCheckout.map(([sessionSid, data]) => (
                            <Options.Item
                                key={`session_recently_exited_${sessionSid}`}
                                beforeLabelContentVAlign="start"
                                afterLabelContentVAlign="start"
                                variant={focusedSessionSid === sessionSid ? "selected" : ""}
                            >
                                <Flex justifyItems="space-between" padding="none">
                                    {/* Waiting timer */}
                                    <Flex.Item width="3.2rem">
                                        <Pill
                                            color="primary"
                                            themeOverride={{
                                                textFontSize: '0.7rem'
                                            }}><TimeElapsed since={data.inCheckoutAt} /></Pill>
                                    </Flex.Item>

                                    {/* Student name */}
                                    <Flex.Item
                                        margin="0 0 0 xx-small"
                                        textAlign="start"
                                        maxWidth="65%"
                                        shouldGrow
                                    >
                                        <RenderName
                                            firstName={data.firstName}
                                            lastName={data.lastName}
                                        />
                                    </Flex.Item>

                                    {/* Options menu */}
                                    <Flex.Item width="2rem" textAlign="end">
                                        {focusedSessionSid === sessionSid ? (
                                            <View margin="0 xx-small 0 0">
                                                <IconEyeLine />
                                            </View>
                                        ) : (<>{renderOptionsMenu(sessionSid)}</>)
                                        }
                                    </Flex.Item>
                                </Flex>
                            </Options.Item>
                        ))}

                    </Options>
                </View>
            ) : (
                <View as="div" padding="small" textAlign="center">
                    <Text>No sessions to review</Text>
                </View>
            )}
        </ToggleGroup>
    );
};

export default RecentlyExited;
